import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, DOBInput, FieldWrapper, FormError, H3, Input, Modal, ModalContent, ModalFooter, ModalFormLayout, ModalHeader, ModalTitle, Password, Text } from 'components';
import ModalFactory from 'components/Modal/ModalFactory';
import { theme, toRem } from 'design';
import { AvatarUploadForm } from 'forms';
import { validation } from 'lib';
import validations from 'lib/Validations';
import { DeleteAccount } from 'modals';
import { routeConstants } from 'routes';
import Form from 'screens/Error/Form';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { logOutRequest } from 'store/App';
import { selectEmployers } from 'store/Employers';
import { IStore } from 'store/types';
import { deleteUserRequest, selectCurrentUser, updateDateOfBirthRequest, updateUsernameRequest, updateUserPasswordRequest, User } from 'store/User';
import { IApiErrorPayloadWithCode, IUpdateUserPasswordPayloadProps, IUpdateUserPayload, RequestStatus } from 'types';
import { formatDateForApi, formatDateForUI } from 'utils/date';
import { FormEmail, FormMobile, SettingsContent, SettingsContentLayout, SettingsFieldWrapper, SettingsGroup, SettingsPageHeader, SettingsSection, SettingsSectionContent, SettingsSectionHeader, SubmitGroup } from './components';

const FormErrorLeftAligned = styled(FormError).attrs({ size: 'small' })`
  text-align: left !important;
  width: 100%;
  margin: 0 0 0 ${toRem(theme.space.lg)} !important;
`;

const SettingsSubmitGroup = styled(SubmitGroup)`
  flex-flow: row-reverse nowrap;
  justify-content: flex-end;
  align-items: center;
  grid-column: 1/-1;
`;

const validationSchema = Yup.object().shape({
  firstName: validations.firstNameValidationSchema,
  lastName: validations.lastNameValidationSchema,
});

const DOBValidationSchema = Yup.object().shape(validations.DOBValidationSchema);

const passwordValidationSchema = Yup.object().shape({
  password: validation.passwordValidationSchema,
  existing_password: Yup.string().required('Current password is required.'),
});

type ModalContentTypes = undefined | 'deleteAccount' | 'dobUpdate' | 'emailUpdate' | 'mobileUpdate' | 'passwordNew' | 'passwordCreate';

const AccountSettings = () => {
  // Modal State
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContentTypes>(undefined);

  // Data
  const dispatch = useDispatch();
  const apiErrorsDOB = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_DATE_OF_BIRTH_REQUEST)) as IApiErrorPayloadWithCode;
  const apiErrorsUpdateName = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_USER_NAME_REQUEST)) as IApiErrorPayloadWithCode;
  const apiErrorsUpdatePassword = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_USER_PASSWORD_REQUEST)) as IApiErrorPayloadWithCode;

  const { firstName, lastName, id: userId, dateOfBirth } = useSelector(selectCurrentUser());
  const organizations = useSelector(selectEmployers);
  const userNameLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_USER_NAME_REQUEST));
  const DOBLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_DATE_OF_BIRTH_REQUEST));
  const userPasswordLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_USER_PASSWORD_REQUEST));
  const DOBStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, User.UPDATE_DATE_OF_BIRTH_REQUEST));
  const deleteUserStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, User.DELETE_USER_REQUEST));
  const deleteUserLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.DELETE_USER_REQUEST));

  const DOBFormik = useFormik({
    initialValues: {
      dateOfBirth: formatDateForUI(dateOfBirth) || '',
    },
    onSubmit: (values: IUpdateUserPayload) => {
      dispatch(
        updateDateOfBirthRequest(
          {
            ...values,
            dateOfBirth: values.dateOfBirth && formatDateForApi(values.dateOfBirth),
          },
          'Date of Birth updated successfully'
        )
      );
    },
    validateOnBlur: true,
    validationSchema: DOBValidationSchema,
    enableReinitialize: true,
  });

  const basicInfoFormik = useFormik({
    initialValues: {
      firstName: firstName || '',
      lastName: lastName || '',
    },
    onSubmit: (values: IUpdateUserPayload) => {
      dispatch(updateUsernameRequest({ ...values }, 'Name updated successfully'));
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const passwordFormik = useFormik({
    initialValues: {
      userId,
      password: '',
      existing_password: '',
    },
    onSubmit: (values: IUpdateUserPasswordPayloadProps) => {
      dispatch(updateUserPasswordRequest(values, 'Password updated successfully'));
    },
    validateOnBlur: true,
    validationSchema: passwordValidationSchema,
    enableReinitialize: true,
  });

  const { resetForm } = passwordFormik;
  useEffect(() => {
    if (!userPasswordLoading) resetForm();
  }, [userPasswordLoading, resetForm]);

  const validateField = (field: 'firstName' | 'lastName') => Boolean(basicInfoFormik.touched[field] && basicInfoFormik.errors[field]);

  const validateDOBField = (field: 'dateOfBirth') => Boolean(DOBFormik.touched[field] && DOBFormik.errors[field]);

  const validatePasswordField = (field: 'existing_password' | 'password') => Boolean(passwordFormik.touched[field] && passwordFormik.errors[field]);

  // DOB Modal
  const DOBModalTitle = 'Update Date of Birth';
  const DOBModalChildren = (
    <ModalFormLayout>
      <FieldWrapper label="Date of Birth" message={validateDOBField('dateOfBirth') ? DOBFormik.errors.dateOfBirth : ''} hasError={validateDOBField('dateOfBirth')}>
        <DOBInput id="dateOfBirth" disabled={DOBLoading} type="text" placeholder="MM-DD-YYYY" {...DOBFormik.getFieldProps('dateOfBirth')} />
      </FieldWrapper>
      {apiErrorsDOB?.errorMessages && apiErrorsDOB.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}
    </ModalFormLayout>
  );

  const DOBFormProps = {
    formId: User.UPDATE_DATE_OF_BIRTH_REQUEST,
    onSubmit: DOBFormik.handleSubmit,
  };

  const DOBModalFooter = <Button type="submit" appearance="primary" size="large" label="Update" disabled={DOBLoading || !(DOBFormik.isValid && DOBFormik.dirty)} />;

  const handleDelete = (dob: string) => {
    dispatch(deleteUserRequest({ dateOfBirth: formatDateForApi(dob) }, 'Successfully deleted account'));
  };

  useEffect(() => {
    if (deleteUserStatus === RequestStatus.SUCCESS) dispatch(logOutRequest(`${routeConstants.public.login}?message=logged_out`));
  }, [deleteUserStatus, dispatch]);

  // useModal
  const [showModal, hideModal] = useModal(() => {
    let customModal;
    let formProps;
    let modalTitle;
    let modalChildren;
    let modalFooter;

    const resetModal = () => {
      hideModal();
      setModalContent(undefined);
      dispatch(clearRequest(User.DELETE_USER_REQUEST));
    };

    // There is going to be more options coming later
    switch (modalContent) {
      case 'dobUpdate':
        customModal = false;
        formProps = DOBFormProps;
        modalTitle = DOBModalTitle;
        modalChildren = DOBModalChildren;
        modalFooter = DOBModalFooter;
        break;
      case 'deleteAccount':
        customModal = true;
        modalChildren = <DeleteAccount onClose={resetModal} organizations={organizations} isLoading={deleteUserLoading} onDelete={handleDelete} error={deleteUserStatus === RequestStatus.FAILED} />;
        break;
      default:
        break;
    }

    return (
      <ModalFactory customModal={customModal} formProps={formProps} title={modalTitle || ''} modalFooter={modalFooter} onClose={resetModal} isActive>
        {modalChildren}
      </ModalFactory>
    );
  }, [modalContent, DOBFormik, deleteUserLoading, deleteUserStatus]);

  useEffect(() => {
    if (DOBStatus === RequestStatus.SUCCESS) {
      hideModal();
      setModalContent(undefined);
    }
  }, [DOBStatus, hideModal]);

  const clearFormErrors = useCallback(() => {
    dispatch(clearRequest(User.UPDATE_USER_PASSWORD_REQUEST));
  }, []);

  const onCurrentPasswordFocus = useCallback(() => {
    passwordFormik.setFieldTouched('existing_password', true);
    clearFormErrors();
  }, []);

  return (
    <>
      <SettingsContent data-cy="account-settings-container">
        <SettingsPageHeader />

        <SettingsContentLayout>
          <SettingsSection data-cy="account-settings-avatar">
            <SettingsSectionHeader>
              <H3>Avatar</H3>
            </SettingsSectionHeader>

            <AvatarUploadForm fluidWidth />
          </SettingsSection>

          <SettingsSection data-cy="account-settings-personal-information">
            <SettingsSectionHeader>
              <H3>Personal Information</H3>
            </SettingsSectionHeader>

            <SettingsGroup>
              <Form onSubmit={basicInfoFormik.handleSubmit} encType="multipart/form-data" formId={User.UPDATE_USER_NAME_REQUEST} dataCy="account-settings-personal-information-names-form">
                <SettingsSectionContent columns={2}>
                  <SettingsFieldWrapper label="First Name" message={validateField('firstName') ? basicInfoFormik.errors.firstName : ''} hasError={validateField('firstName')} data-cy="first-name-field">
                    <Input type="text" disabled={userNameLoading} {...basicInfoFormik.getFieldProps('firstName')} />
                  </SettingsFieldWrapper>

                  <SettingsFieldWrapper label="Last Name" message={validateField('lastName') ? basicInfoFormik.errors.lastName : ''} hasError={validateField('lastName')} data-cy="last-name-field">
                    <Input type="text" disabled={userNameLoading} {...basicInfoFormik.getFieldProps('lastName')} />
                  </SettingsFieldWrapper>

                  {apiErrorsUpdateName?.errorMessages && apiErrorsUpdateName.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}
                  <Button label="Update" type="submit" appearance="primary" size="small" disabled={userNameLoading || !(basicInfoFormik.isValid && basicInfoFormik.dirty)} isProcessing={userNameLoading} data-cy="submit-button" />
                </SettingsSectionContent>
              </Form>

              <Form formId={User.UPDATE_DATE_OF_BIRTH_REQUEST} dataCy="account-settings-personal-information-dob-form">
                <SettingsSectionContent columns="auto">
                  <SettingsFieldWrapper label="Date of Birth" data-cy="dob-field">
                    <Input type="text" name="dob" readOnly value={formatDateForUI(dateOfBirth)} style={{ padding: 0, border: 0 }} />
                  </SettingsFieldWrapper>
                  <Button
                    label="Edit"
                    appearance="primary"
                    size="small"
                    onClick={() => {
                      setModalContent('dobUpdate');
                      showModal();
                    }}
                    data-cy="edit-button"
                  />
                </SettingsSectionContent>
              </Form>
            </SettingsGroup>
          </SettingsSection>

          <SettingsSection data-cy="account-settings-contact">
            <SettingsSectionHeader>
              <H3>Contact</H3>
            </SettingsSectionHeader>

            <SettingsGroup>
              <FormEmail />
              <FormMobile />
            </SettingsGroup>
          </SettingsSection>

          <SettingsSection data-cy="account-settings-update-password">
            <Form formId={User.UPDATE_USER_PASSWORD_REQUEST} onSubmit={passwordFormik.handleSubmit} dataCy="account-settings-update-password-form">
              <SettingsSectionHeader>
                <H3>Update Password</H3>
              </SettingsSectionHeader>

              <SettingsSectionContent columns={2}>
                <SettingsFieldWrapper
                  label="Current Password"
                  message={validatePasswordField('existing_password') ? passwordFormik.errors.existing_password : ''}
                  hasError={validatePasswordField('existing_password')}
                  data-cy="current-password-field"
                >
                  <Password disabled={userPasswordLoading} {...passwordFormik.getFieldProps('existing_password')} onFocus={onCurrentPasswordFocus} />
                </SettingsFieldWrapper>

                <SettingsFieldWrapper label="New Password" message={validatePasswordField('password') ? passwordFormik.errors.password : ''} hasError={validatePasswordField('password')} data-cy="new-password-field">
                  <Password disabled={userPasswordLoading} placeholder="New Password" onFocus={clearFormErrors} {...passwordFormik.getFieldProps('password')} />
                </SettingsFieldWrapper>

                <SettingsSubmitGroup>
                  {apiErrorsUpdatePassword?.errorMessages &&
                    !passwordFormik.touched.existing_password &&
                    apiErrorsUpdatePassword.errorMessages.map((error, index) => (
                      <FormErrorLeftAligned key={`error-message-${index}`} data-cy="password-form-error">
                        {error}
                      </FormErrorLeftAligned>
                    ))}

                  <Button
                    label="Update"
                    type="submit"
                    appearance="primary"
                    size="small"
                    disabled={userPasswordLoading || !(passwordFormik.isValid && passwordFormik.dirty)}
                    isProcessing={userPasswordLoading}
                    data-cy="update-password-button"
                  />
                </SettingsSubmitGroup>
              </SettingsSectionContent>
            </Form>
          </SettingsSection>

          <SettingsSection data-cy="account-settings-delete-account">
            <SettingsSectionHeader>
              <H3>Delete Account</H3>
            </SettingsSectionHeader>

            <SettingsSectionContent>
              <Text size="small">If you delete your account you will no longer be able to send and receive payments. This action cannot be undone.</Text>

              <Button
                appearance="destructive"
                size="small"
                label="Delete"
                onClick={() => {
                  setModalContent('deleteAccount');
                  showModal();
                }}
              />
            </SettingsSectionContent>
          </SettingsSection>
        </SettingsContentLayout>
      </SettingsContent>

      <Modal
        maxWidth={475}
        isActive={modalActive}
        onClose={() => {
          setModalActive(!modalActive);
          setModalContent(undefined);
          DOBFormik.resetForm();
        }}
      >
        {modalContent === 'passwordCreate' && (
          <>
            <ModalHeader>
              <ModalTitle>Create Password</ModalTitle>
            </ModalHeader>

            <ModalContent>
              <ModalFormLayout>
                <FieldWrapper label="Password">
                  <Password />
                </FieldWrapper>
              </ModalFormLayout>
            </ModalContent>

            <ModalFooter columns={1}>
              <Button type="button" appearance="primary" size="large" label="Create password" onClick={() => setModalContent('emailUpdate')} />
            </ModalFooter>
          </>
        )}
      </Modal>
    </>
  );
};

export default AccountSettings;
